import { useState, useMemo } from "react";
import { Column } from "primereact/column";
import { PlusCircleIcon } from "@phosphor-icons/react";

import type { BalanceUiT } from "../domain/ui/balance.model";

import { bodyClassName, headerClassName } from "assets/customStyles";

import {
  BALANCES_COLUMNS,
  EXPORT_COLUMNS,
  TABLE_COLUMN_KEYS,
} from "../constants/columns";
import { GeneralNames } from "namesConstants/names";

import { ColaboraTable, TableBodyAction } from "components/v2";
import { FullLoading } from "components/FullLoading";
import { Toast } from "components/toast";
import { BALANCES_STRINGS } from "../constants/strings";

import { useCollaborators } from "shared/hooks";
import { formatEntryDate } from "shared/hooks/helpers";
import { useExportToExcel } from "shared/hooks/useExportToExcel";
import { useTablePagination } from "shared/hooks/useTablePagination";
import { useBalancesTable } from "../hooks/useBalancesTable";
import { useTableBehavior } from "hooks/useTableBehavior";
import useReload from "hooks/useReload";

import AccrualsDialog from "./AccrualsDialog";
import InitialLoadDialog from "./InitialLoadDialog";
import TableForm from "./TableForm";

const BalancesTable = () => {
  const { data: collaborators = [], isLoading: collaboratorsLoading } =
    useCollaborators();

  const { page, pageSize, paginationProps } = useTablePagination();

  const {
    data: balances,
    isLoading: balancesLoading,
    isRefetching: balancesRefetching,
    refetch: refetchBalances,
  } = useBalancesTable({
    page,
    pageSize,
  });

  useReload([refetchBalances]);

  const loadingState =
    collaboratorsLoading || balancesLoading || balancesRefetching;

  // Indicates whether at least one collaborator has an accrued balance available
  const hasBalances =
    balances?.data?.some((b) => b.totalAvailableDays !== "0 días") === true;

  // Indicates whether all collaborators have accrued balances available
  const allCollaboratorsHaveBalances =
    (balances?.data?.length ?? 0) > 0 &&
    balances?.data?.every((b) => b.totalAvailableDays !== "0 días") === true;

  const [showInitialLoadDialog, setShowInitialLoadDialog] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<BalanceUiT | null>(null);

  const { exportToExcel, isExporting } = useExportToExcel({
    filename: "SaldosVacaciones",
    reportName: "Saldos de vacaciones",
    columns: EXPORT_COLUMNS,
    transformRow: (row) => ({
      ...row,
      [TABLE_COLUMN_KEYS.SENIORITY_DATE_TIMESTAMP]: formatEntryDate(
        row[TABLE_COLUMN_KEYS.SENIORITY_DATE] as string,
      ),
    }),
  });

  const handleOnOpenDialog = () => {
    setShowInitialLoadDialog(true);
  };

  const handleOnHideDialog = () => {
    setShowInitialLoadDialog(false);
  };

  const {
    searchTextFilter,
    sort,
    resetSearchTextFilter,
    setSearchTextFilter,
    setSort,
  } = useTableBehavior({
    initialSort: [{ field: TABLE_COLUMN_KEYS.CODE, order: 1 }],
  });

  const sortedData = useMemo(() => {
    const items = balances?.data ?? [];
    if (!sort?.length) return items;

    return [...items].sort((a, b) => {
      for (const { field, order } of sort) {
        const aVal = a[field as keyof typeof a];
        const bVal = b[field as keyof typeof b];

        if (aVal === bVal) continue;
        if (aVal == null) return order;
        if (bVal == null) return -order;

        const cmp =
          typeof aVal === "number" && typeof bVal === "number"
            ? aVal - bVal
            : String(aVal).localeCompare(String(bVal), undefined, {
                numeric: true,
                sensitivity: "base",
              });

        if (cmp !== 0) return cmp * order;
      }
      return 0;
    });
  }, [balances?.data, sort]);

  const handleExportToExcel = async () => {
    if (isExporting) return;

    if (!sortedData.length) {
      Toast("info", BALANCES_STRINGS.EXPORT_NO_DATA);
      return;
    }

    try {
      await exportToExcel({ data: sortedData });
      Toast("success", BALANCES_STRINGS.EXPORT_SUCCESS);
    } catch (error) {
      Toast(
        "error",
        BALANCES_STRINGS.EXPORT_ERROR,
        (error as Error).message || "No se pudo generar el archivo",
      );
    }
  };

  const actionsBodyTemplate = (rowData: BalanceUiT) => {
    return (
      <TableBodyAction
        icon={<PlusCircleIcon size={30} />}
        toolTip={BALANCES_STRINGS.TEXT_TOOLTIP_EDIT}
        action={() => setSelectedCollaborator(rowData)}
      />
    );
  };

  return (
    <section
      className="h-full w-full border rounded-md shadow-2xl p-2"
      aria-label="Saldos de vacaciones"
      aria-busy={loadingState || isExporting}
      data-testid="balances-table-section"
    >
      <div aria-live="assertive" aria-atomic="true">
        {isExporting && <FullLoading loadingState />}
      </div>
      <div className="w-full h-full flex flex-col gap-2">
        <TableForm
          setSearchTextFilter={setSearchTextFilter}
          resetSearchTextFilter={resetSearchTextFilter}
          onExportToExcel={handleExportToExcel}
          onOpenDialog={handleOnOpenDialog}
        />
        <ColaboraTable
          data={sortedData}
          isLoadingData={loadingState}
          sort={sort}
          onChangeSort={setSort}
          filters={searchTextFilter}
          emptyMessage={GeneralNames.GeneralEmptyMessage}
          currentPageReportTemplate={
            loadingState
              ? BALANCES_STRINGS.TEXT_TABLE_LOADING
              : BALANCES_STRINGS.TEXT_TABLE_RESULTS
          }
          {...paginationProps(
            balances?.meta?.totalPages,
            balances?.meta?.totalItems,
          )}
          globalFilterFields={[
            TABLE_COLUMN_KEYS.CODE,
            TABLE_COLUMN_KEYS.FULL_NAME,
          ]}
        >
          {BALANCES_COLUMNS.map((col) => (
            <Column
              key={`${col.field}_balances`}
              headerClassName={headerClassName}
              style={col.style}
              bodyStyle={col.bodyStyle}
              field={col.field}
              header={col.header}
              body={col.body}
              bodyClassName={bodyClassName}
              align={col.align}
              sortable={col.sortable}
              alignFrozen="left"
              frozen={col.frozen}
            />
          ))}
          <Column
            headerClassName={headerClassName}
            style={{ minWidth: "6rem", width: "6rem" }}
            header="Abonos"
            body={actionsBodyTemplate}
            bodyClassName={bodyClassName}
            align="center"
            alignFrozen="right"
            frozen
          />
        </ColaboraTable>
      </div>
      {showInitialLoadDialog && (
        <InitialLoadDialog
          onHide={handleOnHideDialog}
          collaborators={collaborators}
          sort={sort}
          hasBalances={hasBalances}
          allCollaboratorsHaveBalances={allCollaboratorsHaveBalances}
          onImportSuccess={refetchBalances}
        />
      )}
      {selectedCollaborator && (
        <AccrualsDialog
          onHide={() => setSelectedCollaborator(null)}
          collaborator={selectedCollaborator}
        />
      )}
    </section>
  );
};

export default BalancesTable;
